import { useState } from 'react';
import { useInvoices } from '@/hooks/useInvoices';
import { isOverdue } from '@m/ui/StatusPill';
import { useQuery } from '@tanstack/react-query';
import { fetchReport, fetchReportOverview } from '@/api/misc';
import { get } from '@/lib/api';
import { hoursShort, toDayString } from '@/lib/format';
import type { ReportFilters } from '@/types';
import { Screen } from '@m/ui/Screen';
import { Card, Group, Row } from '@m/ui/List';
import { ChipRow, Skeleton, Stat } from '@m/ui/Controls';
import { Sheet, SheetBack } from '@m/ui/Sheet';
import { wholeMoney } from '../clients/useUnbilled';

type Period = 'month' | 'last-month' | 'quarter' | 'year';

function range(p: Period): Required<Pick<ReportFilters, 'dateFrom' | 'dateTo'>> {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const d = (yy: number, mm: number, dd: number) => toDayString(new Date(yy, mm, dd));
  switch (p) {
    case 'month':
      return { dateFrom: d(y, m, 1), dateTo: d(y, m + 1, 0) };
    case 'last-month':
      return { dateFrom: d(y, m - 1, 1), dateTo: d(y, m, 0) };
    case 'quarter': {
      const q = Math.floor(m / 3) * 3;
      return { dateFrom: d(y, q, 1), dateTo: d(y, q + 3, 0) };
    }
    case 'year':
      return { dateFrom: d(y, 0, 1), dateTo: d(y, 11, 31) };
  }
}

/** S13. The month at a glance; the full report builder stays on the desktop. */
export function ReportsScreen() {
  const [period, setPeriod] = useState<Period>('month');
  const [people, setPeople] = useState(false);
  const filters = range(period);
  // Overdue is "sent and past due" today, whatever the period: the same rule
  // the Invoices tab uses, so the two screens never disagree.
  const sent = useInvoices({ status: 'sent', pageSize: 100 });
  const overdueCount = (sent.data?.items ?? []).filter(isOverdue).length;

  const overview = useQuery({
    queryKey: ['mobile', 'reports', 'overview', filters],
    queryFn: ({ signal }) => fetchReportOverview(filters, signal)
  });
  const hours = useQuery({
    queryKey: ['mobile', 'reports', 'hours-by-client', filters],
    queryFn: ({ signal }) => fetchReport('hours-by-client', filters, signal)
  });
  // /reports/invoiced lists invoices with their client's name; summed per client here.
  const billed = useQuery({
    queryKey: ['mobile', 'reports', 'invoiced', filters],
    queryFn: async ({ signal }) => {
      const raw = await get<{ invoices?: { clientName: string | null; totalAmount: number }[] }>(
        '/api/reports/invoiced',
        { startDate: filters.dateFrom, endDate: filters.dateTo },
        signal
      );
      const m = new Map<string, number>();
      for (const i of raw.invoices ?? []) {
        const k = i.clientName ?? 'Unknown';
        m.set(k, (m.get(k) ?? 0) + (Number(i.totalAmount) || 0));
      }
      return [...m.entries()].sort((a, b) => b[1] - a[1]);
    }
  });
  const byPerson = useQuery({
    queryKey: ['mobile', 'reports', 'hours-by-employee', filters],
    queryFn: ({ signal }) => fetchReport('hours-by-employee', filters, signal),
    enabled: people
  });

  const o = overview.data;
  const top = (billed.data ?? []).slice(0, 6);
  const max = Math.max(1, ...top.map(([, v]) => v));

  return (
    <Screen
      title="Reports"
      back="/more"
      backLabel="More"
    >
      <ChipRow
        value={period}
        onChange={setPeriod}
        options={[
          { value: 'month', label: 'This month' },
          { value: 'last-month', label: 'Last month' },
          { value: 'quarter', label: 'Quarter' },
          { value: 'year', label: 'Year' }
        ]}
      />

      <div className="grid grid-cols-2 gap-2">
        {o ? (
          <>
            <Stat label="Billed" value={wholeMoney(o.invoicedThisMonth)} />
            <Stat label="Collected" value={wholeMoney(o.revenueThisMonth)} />
            <Stat label="Hours logged" value={hours.data ? hoursShort(hours.data.totals.hours) : '—'} />
            <Stat label="Unbilled" value={wholeMoney(o.uninvoicedAmount)} />
          </>
        ) : (
          Array.from({ length: 4 }, (_, i) => <Skeleton key={i} className="h-[74px] rounded-2xl" />)
        )}
      </div>

      <Card className="flex flex-col gap-3">
        <span className="text-body font-semibold">Billed by client</span>
        {billed.isPending ? (
          <Skeleton className="h-28" />
        ) : top.length === 0 ? (
          <span className="text-secondary text-[var(--text-muted)]">Nothing invoiced in this period.</span>
        ) : (
          <div className="flex flex-col gap-2.5 text-secondary">
            {top.map(([name, amount]) => (
              <div key={name} className="flex flex-col gap-1">
                <span className="flex justify-between gap-3">
                  <span className="truncate">{name}</span>
                  <span className="tnum shrink-0 text-[var(--text-muted)]">{wholeMoney(amount)}</span>
                </span>
                <span className="block h-2 rounded bg-[var(--page)]">
                  <span className="block h-2 rounded bg-[var(--accent)]" style={{ width: `${Math.max(2, (amount / max) * 100)}%` }} />
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Group>
        <Row
          to="/invoices?filter=overdue"
          title="Aging"
          detail={
            overdueCount > 0 ? (
              <span className="text-orange-700 dark:text-orange-300">{overdueCount} overdue</span>
            ) : (
              'Nothing overdue'
            )
          }
        />
        <Row title="Hours by person" onClick={() => setPeople(true)} chevron />
      </Group>

      <Sheet open={people} onClose={() => setPeople(false)} full title="Hours by person" leading={<SheetBack label="Reports" onClick={() => setPeople(false)} />}>
        {byPerson.isPending ? (
          <Skeleton className="h-40 rounded-2xl" />
        ) : (
          <Group header={`${byPerson.data?.rows.length ?? 0} people`} headerRight={`${hoursShort(byPerson.data?.totals.hours ?? 0)} h`}>
            {(byPerson.data?.rows ?? []).map((r) => (
              <Row
                key={r.key}
                title={r.label}
                subtitle={r.amount ? wholeMoney(r.amount) : undefined}
                trailing={<span className="tnum font-medium">{hoursShort(r.hours)} h</span>}
              />
            ))}
          </Group>
        )}
      </Sheet>
    </Screen>
  );
}
